import dedent from 'ts-dedent'
import { v4 as uuidv4 } from 'uuid'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import {
  SINGLE_OUTPUT_NAME,
  SingleOutputWorkflowComponent
} from './common/singleoutputcomponent'

const RESERVED_COLUMNS = ['geoid', 'h3', 'quadbin', 'index']

class CommercialHotspots extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.commercialhotspots',
        group: 'Statistics',
        title: 'Hotspot Analysis',
        description: `This component is used to locate hotspot areas by calculating a combined Getis-Ord Gi* statistic using a uniform kernel over several variables.`
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          description: 'The table with the input data',
          optional: false
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          title: 'Column with unique geographic identifier',
          description: `The name of the column with the indexes. It has to be one of 'geoid', 'index ,'h3', 'quadbin'`,
          allowedColumns: RESERVED_COLUMNS,
          optional: false
        },
        {
          name: 'weights',
          type: WorkflowComponentParamType.ColumnNumber,
          title: 'Weights for the weighted average',
          description:
            'The weights associated with each of the variables. These weights can take any value but will be normalized to sum up to 1',
          excludedColumns: RESERVED_COLUMNS,
          enableReverse: true,
          noDefault: true
        },
        {
          name: 'kring',
          type: WorkflowComponentParamType.Number,
          title: 'Size of the k-ring for the spatial weight matrix',
          description:
            'This defines the area around each cell that will be taken into account to compute its Gi* statistic',
          optional: true,
          min: 1,
          default: 3
        },
        {
          name: 'kernel',
          type: WorkflowComponentParamType.Selection,
          title: 'Kernel function',
          description: 'Kernel function for spatial weights',
          options: [
            'uniform',
            'triangular',
            'quadratic',
            'quartic',
            'gaussian',
            'bounded_gaussian',
            'inverse',
            'inverse_square',
            'exponential'
          ],
          providers: [Provider.DatabricksRest]
        },
        {
          name: 'clustertype',
          type: WorkflowComponentParamType.Selection,
          title: 'Kernel function',
          description: 'Kernel function for spatial weights',
          options: ['hotspots', 'coldspots', 'all'],
          providers: [Provider.DatabricksRest]
        },
        {
          name: 'pvaluethresh',
          type: WorkflowComponentParamType.Number,
          title: 'Significance level',
          description:
            'Threshold for the Gi* value significance, ranging from 0 (most significant) to 1 (least significant). Cells with a p-value above this threshold will not be returned',
          optional: true,
          min: 0,
          max: 1,
          default: 0.05
        }
      ]
    )
    this.supportedProviders = [Provider.BigQuery, Provider.DatabricksRest]
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams) {
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      preparedParams,
      'result',
      codeType
    )
    const reversed_tablename_bq = tablename.replace('result', 'reversed')
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    const source = preparedParams.source as string
    let query = ''

    const weightsArray = (preparedParams.weights &&
      JSON.parse(preparedParams.weights as string)) as Array<Array<any>>
    const selectColumns = Object.entries(weightsArray).map(([_, val]) => {
      return val[2] ? `${val[0]} * -1 AS ${val[0]}` : ` ${val[0]}`
    })
    const selectColumnsString = selectColumns.length
      ? `, ${selectColumns.join(', ')}`
      : ''
    const inputquery = `SELECT ${
      preparedParams.indexcol
    }${selectColumnsString} FROM ${connection.quoteName(source)}`

    switch (connection.type) {
      case Provider.BigQuery: {
        let outTable = tablename
        let inTable = reversed_tablename_bq
        let copyFromTempTable = ''
        if (codeType === WorkflowCodeType.StoredProcedure) {
          outTable = `${connection.tempLocation}.__temp_${uuidv4()
            .split('-')
            .join('_')}`
          inTable = `${outTable}_reversed`
          copyFromTempTable = connection.getCreateTempTableQuery(
            tablename,
            `SELECT * FROM ${connection.quoteName(outTable)}`,
            codeType,
            null
          )
        }
        const variables_str = weightsArray.map((c) => `'${c[0]}'`).join(', ')
        const weights_str = weightsArray
          .map((c) => Number(c[1]).toFixed(5))
          .join(', ')
        query = dedent`
              DECLARE grid_type STRING;
              DECLARE grid_resolution INT64;
              CALL ${at}.__CHECK_GRID_INDEX_COLUMN(
                'SELECT * FROM \`${source}\`',
                '${preparedParams.indexcol}',
                grid_type,
                grid_resolution
              );
              IF grid_type != 'unsupported' THEN
                CREATE OR REPLACE TABLE ${connection.quoteName(inTable)} AS (
                  ${inputquery}
                );
                CALL ${at}.COMMERCIAL_HOTSPOTS(
                    '${inTable}',
                    '${outTable}',
                    '${preparedParams.indexcol as string}',
                    IF(grid_type = 'h3', 'h3', 'quadbin'),
                    [${variables_str}],
                    [${weights_str}],
                    ${preparedParams.kring},
                    ${preparedParams.pvaluethresh}
                );
              ELSE
                RAISE USING MESSAGE = 'Invalid spatial index column, please select a column that contains h3 or quadbin indexes.';
              END IF;
              ${copyFromTempTable}
        `
        break
      }

      case Provider.DatabricksRest: {
        const weights_dict = Object.fromEntries(
          weightsArray.map(([c, w, r]) => [c, Number(w)])
        )
        query = dedent`
        from carto_statistics.HOTSPOT_ANALYSIS import main as hotspot_analysis
        hotspot_analysis(
            '${inputquery}',
            '${preparedParams.indexcol as string}',
            ${JSON.stringify(weights_dict, null, 2)},
            '${connection.quoteName(tablename)}',
            kring_size=${preparedParams.kring},
            kernel_function='${preparedParams.kernel}',
            pvalue_thr=${preparedParams.pvaluethresh},
            cluster_type='${preparedParams.clustertype}'
          )
        `
        break
      }
    }

    query = connection.wrapCodeInIfTableExistsBlock(
      query,
      [tablename],
      codeType
    )

    if (connection.type === Provider.DatabricksRest) {
      query = dedent`
      ${query}
      ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        connection.optimizationStatsQuery(tablename)
      )}
    `
    }

    const outputs: Record<string, string> = {}
    outputs[SINGLE_OUTPUT_NAME] = tablename
    return {
      outputs,
      query
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  getVersion(provider: Provider): string {
    switch (provider) {
      case Provider.BigQuery:
        return '2'
      default:
        return '1'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    const indexType = columns.source.find(
      (c) => c.name === params.indexcol
    )?.type
    return [
      {
        name:
          connection.type === Provider.DatabricksRest
            ? (params.indexcol as string)
            : 'index',
        type: indexType as ColumnDataType
      },
      {
        name: 'combined_gi',
        type: ColumnDataType.Number
      },
      {
        name: 'p_value',
        type: ColumnDataType.Number
      }
    ]
  }
}

export { CommercialHotspots }
